/**
 * useEditorBranch
 *
 * Handles merge, abandon, and View Main toggle for the active tab's branch.
 * Mirrors the toggle_view_main / merge_branch / abandon_branch flow from the backend LiveView.
 */

import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMergeBranch, useAbandonBranch } from '@/hooks/metadata_v1';
import { useMetadataEditorStore } from '@/stores/metadataEditorStore';
import type { BranchConflict, ResolvedConflictInput } from '@/services/metadata_v1/types';
import type { EditorTab } from '@/stores/metadataEditorStore';

export interface UseEditorBranchReturn {
  // View Main toggle
  handleToggleViewMain: () => void;

  // Merge
  handleMerge: () => void;
  isMerging: boolean;

  // Abandon
  handleAbandon: () => void;
  isAbandoning: boolean;

  // Conflict state — caller renders conflict UI when non-null
  conflict: BranchConflict | null;
  handleResolveConflict: (resolution: ResolvedConflictInput) => void;
  handleDismissConflict: () => void;
}

export function useEditorBranch(activeTab: EditorTab | undefined): UseEditorBranchReturn {
  const setViewingMain = useMetadataEditorStore((state) => state.setViewingMain);
  const setBranchForTab = useMetadataEditorStore((state) => state.setBranchForTab);
  const syncTabContentFromServer = useMetadataEditorStore((state) => state.syncTabContentFromServer);

  const [conflict, setConflict] = useState<BranchConflict | null>(null);

  // ── View Main toggle ──────────────────────────────────────────────────────
  // Swaps what the editor displays between branch content and cached main content.
  // Does NOT modify the actual tab content — just flips viewingMain flag.
  const handleToggleViewMain = useCallback(() => {
    if (!activeTab?.activeBranch) return;
    setViewingMain(activeTab.id, !activeTab.viewingMain);
  }, [activeTab, setViewingMain]);

  // ── Merge ─────────────────────────────────────────────────────────────────
  const { mutate: mergeBranch, isPending: isMerging } = useMergeBranch({
    onSuccess: () => {
      if (!activeTab) return;
      syncTabContentFromServer(activeTab.file.id, activeTab.mainContent, false);
      setBranchForTab(activeTab.id, null);
      setConflict(null);
      toast.success(`Branch merged successfully`);
    },
    onConflict: (c) => {
      setConflict(c);
    },
    onError: (error) => {
      toast.error(`Merge failed: ${error.message}`);
    },
  });

  const handleMerge = useCallback(() => {
    if (!activeTab?.activeBranch) return;
    mergeBranch({ branchId: activeTab.activeBranch.id });
  }, [activeTab, mergeBranch]);

  const handleResolveConflict = useCallback((resolution: ResolvedConflictInput) => {
    if (!activeTab?.activeBranch) return;
    mergeBranch({
      branchId: activeTab.activeBranch.id,
      resolvedConflict: resolution,
    });
  }, [activeTab, mergeBranch]);

  const handleDismissConflict = useCallback(() => setConflict(null), []);

  // ── Abandon ───────────────────────────────────────────────────────────────
  const { mutate: abandonBranch, isPending: isAbandoning } = useAbandonBranch(
    activeTab?.file.id,
    {
      onSuccess: () => {
        if (!activeTab) return;
        // Revert editor to main content
        syncTabContentFromServer(activeTab.file.id, activeTab.mainContent, false);
        setBranchForTab(activeTab.id, null);
        toast.success(`Branch discarded`);
      },
      onError: (error) => {
        toast.error(`Abandon failed: ${error.message}`);
      },
    }
  );

  const handleAbandon = useCallback(() => {
    if (!activeTab?.activeBranch) return;
    abandonBranch({ branchId: activeTab.activeBranch.id });
  }, [activeTab, abandonBranch]);

  return {
    handleToggleViewMain,
    handleMerge,
    isMerging,
    handleAbandon,
    isAbandoning,
    conflict,
    handleResolveConflict,
    handleDismissConflict,
  };
}
